import * as React from 'react';

// Hooks
import { useNavigate, useLocation } from 'react-router-dom';

// Utils
import {
  isWebBridgeSupported,
  configurePage,
  popWebView,
  navigate as navigateWebView,
  navigateAppRL,
  webNotify,
  logout,
  trackBIEvent,
  openExternalLink,
  fetchToBDataFromNative,
  isBiometricSupportByDevice,
  isEnableBiometric,
  enableBiometric,
  disableBiometric,
  reLoginWithBiometric,
  getDataFromLocalStorage,
  setDataToLocalStorage,
  getNativeCookie,
  checkWhatsappAvailable,
  openWhatsapp,
  getAppInfo,
  getAvailableToBAuthMethods,
  authWith3PServiceByToken,
} from '@shopee/web-bridge-sdk';
import { parseQueryStringToObj } from '@shopee/url-util';
import { isFeatureEnabled } from '@shopee-user/business-customization-logic';
import { callWithTimeout } from './utils';

// Constants
import {
  EnableBiometricAction,
  BiometricStatus,
  AuthMethod as ToBAuthMethod,
} from '@shopee/web-bridge-sdk';
import { Feature } from '@shopee-user/feature-toggle';
import {
  AuthMethod,
  AuthenticationSource,
  DEFAULT_BRIDGE_TIMEOUT,
  PAGE_EXIT_STATE,
  RELOGIN_INFO_LOCAL_STORAGE_KEY,
} from './constants';
import { BusinessTarget } from '@shopee-user/global';
import { config } from '@shopee-user/platform';

const { BUSINESS_TARGET } = config;

// Types
import { Locale } from '@shopee/flow-types';
import {
  OpenExternalLinkParams,
  FetchToBDataFromNativeRequest,
  FetchToBDataFromNativeResponse,
  IsBiometricSupportByDeviceResponse,
  IsEnableBiometricParams,
  IsEnableBiometricResponse,
  EnableBiometricParams,
  EnableBiometricResponse,
  DisableBiometricParams,
  DisableBiometricResponse,
  ReLoginWithBiometricParams,
  ReLoginWithBiometricResponse,
  GetDataFromLocalStorageResponse,
  GetNativeCookieParams,
  GetNativeCookieResponse,
  CheckWhatsappAvailableResponse,
  OpenWhatsappParams,
  OpenWhatsappResponse,
  GetAppInfoResponse,
  GetAvailableToBAuthMethodsParams,
  GetAvailableToBAuthMethodsResponse,
  AuthWith3PServiceByTokenParams,
  AuthWith3PServiceByTokenResponse,
} from '@shopee/web-bridge-sdk';
import {
  Props,
  WithUserWebViewProps,
  GetReloginUserInfoResponse,
  SetReloginUserInfoParams,
  SetReloginUserInfoResponse,
  GetAuthConfigResponse,
} from './types';
import { IsBiometricSupportByDeviceParams } from '@shopee/web-bridge-sdk/dist/types/commands/is-biometric-support-by-device';
import { trackWebBridgeCall } from './monitor';
import { setCurrentUrl } from '@tob/shared/utils/redirection';
import logInNonLive from '@tob/shared/utils/log-in-nonlive';

export function withUserWebView({ shouldShowFullScreen = false }: Props) {
  return function <P>(
    WrappedComponent: React.ComponentType<P & WithUserWebViewProps>
  ): React.ComponentType<P> {
    const ComponentWithWebView = (props: P) => {
      const navigate = useNavigate();
      const location = useLocation();

      const webViewExitWithState = (data: Record<string, any>) => {
        if (isWebBridgeSupported()) {
          const { pageExitState, ...otherData } = data;
          popWebView({
            data: JSON.stringify({ status: pageExitState, ...otherData }),
          });
        } else {
          // TODO @tranct handle the next in URL
          navigate(-1);
        }
      };

      const webViewNavigateBack = () => {
        logInNonLive('webViewNavigateBack() clicked');
        // if we are in WebView and is_initial=true, it means that we are at the entry point
        // the back arrow should close webview
        const { is_initial: isInitialWebView } = parseQueryStringToObj(
          location.search
        );

        if (isInitialWebView === 'true') {
          webViewExitWithState({ pageExitState: PAGE_EXIT_STATE.ABORTED });
        } else {
          // TODO @tranct handle the next in URL
          navigate(-1);
        }
      };

      const webViewNavigateToUrl = (url: string) => {
        logInNonLive(`webViewNavigateToUrl: ${url}`);
        if (isWebBridgeSupported()) {
          navigateWebView({ url });
        } else {
          window.open(url, '_blank', 'noopener noreferrer');
        }
      };

      const webViewNavigateToAppRL = (apprl: string) => {
        if (isWebBridgeSupported()) {
          navigateAppRL({ apprl });
        } else {
          window.open(apprl, '_blank', 'noopener noreferrer');
        }
      };

      const webViewLoginSuccessfully = ({
        nonce,
        fromSource,
        authSource,
        data,
      }: {
        nonce?: string;
        /**
         * fromSource currently is only supported in Android
         * for support in iOS, would need additional support:
         * * from iOS team to add this handling in the native sdk and
         * * from native team to upgrade the native sdk to support this feature.
         * https://confluence.shopee.io/display/SAUT/%5BWEB%5D%5BFE%5D%5BTD%5D+SPX+Driver+BR+Self+Registration+Flow
         */
        fromSource?: AuthenticationSource;
        authSource?: string;
        data?: Record<string, any>;
      }) => {
        if (isWebBridgeSupported()) {
          const tracker = trackWebBridgeCall({
            method: 'webNotify_notifyDidFinishToBLogin',
          });
          webNotify({
            notifyType: 'notifyDidFinishToBLogin',
            nonce: nonce || '',
            fromSource: fromSource || '',
            ...(authSource && { authSource }),
            data,
          });
          tracker.end();
        } else {
          // by right this should not happen, we just put a safe guard
          // so that we can identify issues
          setCurrentUrl({ url: '/', isHardcodedUrl: true });
        }
      };

      // Currently this is only for SPX GTM/G4A tracking requirement
      // No business logic is bound to this notify event
      const webViewNotifySignupSuccess = ({
        fromSource,
        data,
      }: {
        fromSource?: AuthenticationSource;
        data?: Record<string, any>;
      }) => {
        if (
          !isWebBridgeSupported() ||
          !isFeatureEnabled(Feature.WebViewNotifySignupSuccess)
        ) {
          return;
        }

        const tracker = trackWebBridgeCall({
          method: 'webNotify_notifySignupSuccess',
        });
        webNotify({
          notifyType: 'notifySignupSuccess',
          fromSource: fromSource || '',
          data,
        });
        tracker.end();
      };

      const webViewNotifyLoginError = ({
        errorCode,
        errorMsg,
        api,
        locale,
      }: {
        errorCode: number;
        errorMsg: string | undefined;
        api: string | undefined;
        locale: Locale;
      }) => {
        if (
          !isWebBridgeSupported() ||
          !isFeatureEnabled(Feature.WebViewNotifyLoginError)
        ) {
          return;
        }

        const tracker = trackWebBridgeCall({
          method: 'webNotify_notifyToBLoginFailed',
        });
        webNotify({
          notifyType: 'notifyToBLoginFailed',
          cookie: document.cookie,
          errorCode: `${errorCode}`,
          errorMessage: errorMsg || '',
          errorCommand: api || '',
          locale,
        });
        tracker.end();
      };

      const webViewNotifyRefreshTokenAfterChangeCredentials = ({
        nonce,
        fromSource,
      }: {
        nonce?: string;
        /**
         * fromSource currently is only supported in Android
         * for support in iOS, would need additional support:
         * * from iOS team to add this handling in the native sdk and
         * * from native team to upgrade the native sdk to support this feature.
         * https://confluence.shopee.io/display/SAUT/%5BWEB%5D%5BFE%5D%5BTD%5D+SPX+Driver+BR+Self+Registration+Flow
         */
        fromSource?: AuthenticationSource;
      }) => {
        if (
          !isWebBridgeSupported() ||
          !isFeatureEnabled(Feature.NotifyRefreshToken)
        ) {
          console.log('skip notify refresh tob token');
          return;
        }

        if (isFeatureEnabled(Feature.NotifyRefreshTokenLegacy)) {
          webNotify({
            notifyType: 'notifyDidFinishToBLogin',
            nonce: nonce || '',
            fromSource: fromSource || '',
          });
        } else {
          webNotify({
            notifyType: 'notifyRefreshToBToken',
            nonce: nonce || '',
          });
        }
      };

      /**
       * TODO: @haiming.xu @billy.tso @gabybenedicta to confirm with PM whether to use initial_authkey
       * https://jira.shopee.io/browse/SPUS-17282
       *
       *  */
      const webViewNotifyOneCredentialMigrationSuccess = ({
        initialPhone,
        initialAuthKey,
      }: {
        initialPhone?: string;
        initialAuthKey?: string;
      }) => {
        if (!isWebBridgeSupported()) {
          console.log('skip notify one credential migration success');
          return;
        }

        webNotify({
          notifyType: 'notifyOneCredentialAccountMigrationSuccess',
          ...(initialPhone && { initial_phone: initialPhone }),
          ...(initialAuthKey && { initial_authkey: initialAuthKey }),
        } as any); // TODO: fix typing in @shopee/web-bridge-sdk package
      };

      const webViewLogoutSuccessfully = () => {
        if (isWebBridgeSupported()) {
          const tracker = trackWebBridgeCall({
            method: 'logout',
          });
          logout();
          tracker.end();
        } else {
          // by right this should not happen, we just put a safe guard
          // so that we can identify issues
          setCurrentUrl({ url: '/', isHardcodedUrl: true });
        }
      };

      const webViewOpenExternalLink = (
        params: OpenExternalLinkParams,
        callback?: (data: { status?: number }) => void
      ) => {
        if (isWebBridgeSupported()) {
          openExternalLink(params, callback);
        } else {
          window.open(params.url, '_blank', 'noopener noreferrer');
        }
      };

      async function webViewFetchToBDataFromNative<RequestType, ResponseType>({
        scenario,
        requestPayload,
      }: FetchToBDataFromNativeRequest<RequestType>): Promise<
        FetchToBDataFromNativeResponse<ResponseType>
      > {
        if (isWebBridgeSupported()) {
          return fetchToBDataFromNative<RequestType, ResponseType>({
            scenario,
            requestPayload,
          });
        }
        return {
          error: -1,
          error_msg: '',
        };
      }

      async function webViewIsBiometricSupportedByDevice(
        params: IsBiometricSupportByDeviceParams
      ): Promise<IsBiometricSupportByDeviceResponse> {
        if (isWebBridgeSupported()) {
          console.log(
            '[bridge] call isBiometricSupportByDevice: ',
            JSON.stringify(params, null, 2)
          );
          const response = await callWithTimeout(
            isBiometricSupportByDevice(params),
            DEFAULT_BRIDGE_TIMEOUT,
            { errorCode: 1, errorMessage: '', isAvailable: false }
          );
          console.log(
            '[bridge] call isBiometricSupportByDevice response: ',
            JSON.stringify(response, null, 2)
          );
          return response;
        }
        return {
          errorCode: 1,
          errorMessage: '',
          isAvailable: false,
        };
      }

      async function webViewIsBiometricEnabled({
        userId,
        cooldown,
        isEnableFromServer,
        type,
      }: IsEnableBiometricParams): Promise<IsEnableBiometricResponse> {
        if (isWebBridgeSupported()) {
          console.log(
            '[bridge] call webViewIsBiometricEnabled: ',
            JSON.stringify(
              {
                userId,
                cooldown,
                isEnableFromServer,
                type,
              },
              null,
              2
            )
          );
          const response = await callWithTimeout(
            isEnableBiometric({
              userId,
              cooldown,
              isEnableFromServer,
              type,
            }),
            DEFAULT_BRIDGE_TIMEOUT,
            { errorCode: 1, errorMessage: '' }
          );
          console.log(
            '[bridge] call webViewIsBiometricEnabled response: ',
            JSON.stringify(response, null, 2)
          );
          return response;
        }
        return {
          errorCode: 1,
          errorMessage: '',
        };
      }

      async function webViewGetReloginUserInfo(): Promise<
        GetReloginUserInfoResponse
      > {
        if (isWebBridgeSupported()) {
          console.log('[bridge] call getDataFromLocalStorage (key: userInfo)');
          const response = await callWithTimeout<
            GetDataFromLocalStorageResponse
          >(
            getDataFromLocalStorage({
              key: RELOGIN_INFO_LOCAL_STORAGE_KEY,
            }),
            DEFAULT_BRIDGE_TIMEOUT,
            { errorCode: 1, errorMessage: '', data: '' }
          );
          try {
            const result = {
              errorMessage: response.errorMessage,
              errorCode: response.errorCode,
              userInfo: JSON.parse(response.data || '{}'),
            } as GetReloginUserInfoResponse;
            console.log(
              '[bridge] call getDataFromLocalStorage (key: userInfo) response: ',
              JSON.stringify(result, null, 2)
            );
            return result;
          } catch {
            return {
              errorCode: 1,
              errorMessage: '',
            };
          }
        }
        return {
          errorCode: 1,
          errorMessage: '',
        };
      }

      async function webViewSetReloginUserInfo(
        params: SetReloginUserInfoParams
      ): Promise<SetReloginUserInfoResponse> {
        if (isWebBridgeSupported()) {
          try {
            console.log(
              '[bridge] call setDataToLocalStorage (key: userInfo): ',
              JSON.stringify(params, null, 2)
            );
            const response = await callWithTimeout<SetReloginUserInfoResponse>(
              setDataToLocalStorage({
                key: RELOGIN_INFO_LOCAL_STORAGE_KEY,
                value: JSON.stringify(params),
              }),
              DEFAULT_BRIDGE_TIMEOUT,
              { errorCode: 1, errorMessage: '' }
            );
            console.log(
              '[bridge] setDataToLocalStorage (key: userInfo) response: ',
              JSON.stringify(response, null, 2)
            );
            return response;
          } catch {
            return {
              errorCode: 1,
              errorMessage: '',
            };
          }
        }
        return {
          errorCode: 1,
          errorMessage: '',
        };
      }

      async function webViewEnableBiometric(
        params: Omit<EnableBiometricParams, 'action'>
      ): Promise<EnableBiometricResponse> {
        if (isWebBridgeSupported()) {
          console.log(
            '[bridge] call enableBiometric (action: Enable): ',
            JSON.stringify(params, null, 2)
          );
          const response = await enableBiometric({
            ...params,
            action: EnableBiometricAction.Enable,
          });
          console.log(
            '[bridge] enableBiometric (action: Enable) response: ',
            JSON.stringify(response, null, 2)
          );
          return response;
        }
        return {
          errorCode: 1,
          errorMessage: '',
          status: BiometricStatus.ErrorUnknown,
        };
      }

      async function webViewSetupBiometricLater(
        params: Omit<EnableBiometricParams, 'action'>
      ): Promise<EnableBiometricResponse> {
        if (isWebBridgeSupported()) {
          console.log(
            '[bridge] call enableBiometric (action: SetupLater): ',
            JSON.stringify(params, null, 2)
          );
          const response = await callWithTimeout<EnableBiometricResponse>(
            enableBiometric({
              ...params,
              action: EnableBiometricAction.SetupLater,
            }),
            DEFAULT_BRIDGE_TIMEOUT,
            {
              errorCode: 1,
              errorMessage: '',
              status: BiometricStatus.ErrorUnknown,
            }
          );
          console.log(
            '[bridge] enableBiometric (action: SetupLater) response: ',
            JSON.stringify(response, null, 2)
          );
          return response;
        }
        return {
          errorCode: 1,
          errorMessage: '',
          status: BiometricStatus.ErrorUnknown,
        };
      }

      async function webViewDisableBiometric(
        params: DisableBiometricParams
      ): Promise<DisableBiometricResponse> {
        if (isWebBridgeSupported()) {
          console.log(
            '[bridge] call disableBiometric: ',
            JSON.stringify(params, null, 2)
          );
          const response = await disableBiometric(params);
          console.log(
            '[bridge] disableBiometric response: ',
            JSON.stringify(response, null, 2)
          );
          return response;
        }
        return {
          errorCode: 1,
          errorMessage: '',
          status: 1,
        };
      }

      async function webViewReloginWithBiometric(
        params: ReLoginWithBiometricParams
      ): Promise<ReLoginWithBiometricResponse> {
        if (isWebBridgeSupported()) {
          console.log(
            '[bridge] reLoginWithBiometric: ',
            JSON.stringify(params, null, 2)
          );
          const response = await reLoginWithBiometric(params);
          console.log(
            '[bridge] reLoginWithBiometric response: ',
            JSON.stringify(response, null, 2)
          );
          return response;
        }
        return {
          errorCode: 1,
          errorMessage: '',
          status: BiometricStatus.ErrorUnknown,
        };
      }

      async function webViewGetNativeCookie(
        params: GetNativeCookieParams
      ): Promise<GetNativeCookieResponse> {
        if (isWebBridgeSupported()) {
          console.log(
            '[bridge] getNativeCookie: ',
            JSON.stringify(params, null, 2)
          );
          const response = await callWithTimeout<GetNativeCookieResponse>(
            getNativeCookie(params),
            DEFAULT_BRIDGE_TIMEOUT,
            { errorCode: 1, errorMessage: '' }
          );
          console.log(
            '[bridge] getNativeCookie response: ',
            JSON.stringify(response, null, 2)
          );
          return response;
        }
        return {
          errorCode: 1,
          errorMessage: '',
        };
      }

      async function webViewCheckWhatsappAvailable(): Promise<
        CheckWhatsappAvailableResponse
      > {
        const defaultErrorResponse: CheckWhatsappAvailableResponse = {
          errorCode: 1,
          waConsumerAvailable: false,
          waBizAvailable: false,
        };
        if (isWebBridgeSupported()) {
          const response = await callWithTimeout(
            checkWhatsappAvailable(),
            DEFAULT_BRIDGE_TIMEOUT,
            defaultErrorResponse
          );

          return response;
        }

        return defaultErrorResponse;
      }

      async function webViewOpenWhatsapp(
        params: OpenWhatsappParams
      ): Promise<OpenWhatsappResponse> {
        if (isWebBridgeSupported()) {
          const response = await openWhatsapp(params);

          return response;
        }
        return {
          errorCode: 1,
          errorMessage: '',
        };
      }

      async function webViewGetAuthConfig<Method extends AuthMethod>({
        method,
      }: {
        method: ToBAuthMethod;
      }): Promise<GetAuthConfigResponse<Method>> {
        console.log('getToBAuthConfig', isWebBridgeSupported(), method);
        if (isWebBridgeSupported()) {
          const { config, errorCode, errorMessage } = await callWithTimeout<
            GetAuthConfigResponse<Method>
          >(
            new Promise<GetAuthConfigResponse<Method>>(resolve => {
              try {
                // TODO: we need to update @shopee/web-bridge-sdk to add this new bridge function
                // will do later integration
                (window as any).WebViewJavascriptBridge!.callHandler(
                  'getToBAuthConfig',
                  { method },
                  (response: GetAuthConfigResponse<Method>) => {
                    console.log(
                      'webbridge call getToBAuthConfig response',
                      response
                    );
                    resolve(response);
                  }
                );
              } catch (err) {
                console.error('webbridge call getToBAuthConfig error', err);
                resolve({
                  errorCode: 1,
                  errorMessage: err?.message || err?.toString(),
                });
              }
            }),
            DEFAULT_BRIDGE_TIMEOUT,
            { errorCode: 1, errorMessage: 'webbridge function call timeout' }
          );

          return {
            errorCode,
            errorMessage,
            config,
          };
        }

        return {
          errorCode: 1,
          errorMessage: 'webbridge not supported',
        };
      }

      async function webViewGetAvailableToBAuthMethods({
        methods,
      }: GetAvailableToBAuthMethodsParams): Promise<
        GetAvailableToBAuthMethodsResponse
      > {
        if (isWebBridgeSupported()) {
          console.log(
            '[bridge] call getAvailableToBAuthMethods: ',
            JSON.stringify(methods, null, 2)
          );
          const response = await callWithTimeout<
            GetAvailableToBAuthMethodsResponse
          >(getAvailableToBAuthMethods({ methods }), DEFAULT_BRIDGE_TIMEOUT, {
            errorCode: 1,
            errorMessage: 'webbridge function call timeout',
          });
          console.log(
            '[bridge] getAvailableToBAuthMethods response',
            JSON.stringify(response, null, 2)
          );
          return response;
        }

        return {
          errorCode: 1,
          errorMessage: 'webbridge not supported',
        };
      }

      async function webViewAuthWith3PServiceByToken({
        method,
        scopes,
      }: AuthWith3PServiceByTokenParams): Promise<
        AuthWith3PServiceByTokenResponse
      > {
        if (isWebBridgeSupported()) {
          console.log(
            '[bridge] call authWith3PServiceByToken: ',
            JSON.stringify({ method, ...(scopes && { scopes }) }, null, 2)
          );
          /*
           * authWith3PServiceByToken may open an account-selection page when the user has
           * multiple logged-in accounts, followed by a consent page — both are user-driven
           * interactions that can take an indefinite amount of time, so no timeout is applied.
           */
          const response = await authWith3PServiceByToken({
            method,
            ...(scopes && { scopes }),
          });
          console.log(
            '[bridge] authWith3PServiceByToken response',
            JSON.stringify(response, null, 2)
          );
          return response;
        }

        return {
          errorCode: 1,
          errorMessage: 'webbridge not supported',
        };
      }

      async function webViewGetAppInfo(): Promise<GetAppInfoResponse | {}> {
        if (isWebBridgeSupported()) {
          return callWithTimeout(
            new Promise(resolve => {
              const callback = (data: GetAppInfoResponse | {}) => {
                resolve(data);
              };
              getAppInfo(callback);
            }),
            DEFAULT_BRIDGE_TIMEOUT,
            {}
          );
        } else {
          return {};
        }
      }

      const sendTrackingEvent = (events: string) => {
        trackBIEvent({ events });
      };

      // Make fullscreen
      React.useLayoutEffect(() => {
        if (shouldShowFullScreen) {
          // we can remove this urgly logic once all partner users are using new partner app which integrated with ToB sdk
          const isShopeePartner =
            BUSINESS_TARGET === BusinessTarget.ShopeePartner;

          configurePage({
            config: {
              disableReload: 1,
            },
            navbar: {
              isTransparent: 1,
              hideBackButton: 1,
              ...(isShopeePartner
                ? {
                    title: '', // explicitly set empty title, otherwise partner app might display native navbar under some scenarios
                  }
                : {}),
            },
          });
          /**
           * @jay.liangjh
           *
           * This is a tricky way to TRY to solve Partner App fullscreen issue.
           * https://jira.shopee.io/browse/SPB-22446
           *
           * Partner App native will observe web page title changes,
           * and do logics to show/hide native navbar/title if `title` option is not explicitly set to be empty string
           * (then partner native will treat it as null value).
           *
           * The fullscreen issue happens when jump to ToC pages, then back to ToB page by history back.
           * Then native title changing observe will capture the event,
           * because ToC page title is changed to ToB page title (Shopee --> Shopee Partner),
           * then native code will show native title, and it will overlay the web page navbar.
           *
           * In this scenario, `withWebview({ shouldShowFullScreen: true })` doesn't work.
           * By analysising the native logs, configurePage bridge calling is done before native title observe handling
           * (I guess that's caused by native multiple threads, but can't proof)
           *
           * This workaround can't totaly solve the issue without change ToC codes, due to native code execution timeing.
           * ToC configurePage bridge doesn't explicitly set empty title value as well.
           * It's not safe to change ToC configurePage bridge calling, because the impact is unevaluatable:
           *    - @shopee/with-web-view package is used by many places (many teams);
           *    - each native app (without ToB sdk or another unified sdk) has their own bridge implementation;
           * And this issue is not easy to be triggered, it's not worth to do so low level changes.
           */
          if (isShopeePartner && isWebBridgeSupported()) {
            window.document.title = '';
          }
        }
      }, [shouldShowFullScreen]);

      return (
        <WrappedComponent
          {...props}
          webViewNavigateBack={webViewNavigateBack}
          webViewExitWithState={webViewExitWithState}
          webViewNavigateToUrl={webViewNavigateToUrl}
          webViewNavigateToAppRL={webViewNavigateToAppRL}
          webViewLoginSuccessfully={webViewLoginSuccessfully}
          webViewLogoutSuccessfully={webViewLogoutSuccessfully}
          trackBIEvent={sendTrackingEvent}
          webViewNotifySignupSuccess={webViewNotifySignupSuccess}
          webViewNotifyLoginError={webViewNotifyLoginError}
          webViewNotifyRefreshTokenAfterChangeCredentials={
            webViewNotifyRefreshTokenAfterChangeCredentials
          }
          webViewNotifyOneCredentialMigrationSuccess={
            webViewNotifyOneCredentialMigrationSuccess
          }
          webViewOpenExternalLink={webViewOpenExternalLink}
          webViewFetchToBDataFromNative={webViewFetchToBDataFromNative}
          webViewIsBiometricSupportedByDevice={
            webViewIsBiometricSupportedByDevice
          }
          webViewIsBiometricEnabled={webViewIsBiometricEnabled}
          webViewGetReloginUserInfo={webViewGetReloginUserInfo}
          webViewSetReloginUserInfo={webViewSetReloginUserInfo}
          webViewEnableBiometric={webViewEnableBiometric}
          webViewSetupBiometricLater={webViewSetupBiometricLater}
          webViewDisableBiometric={webViewDisableBiometric}
          webViewReloginWithBiometric={webViewReloginWithBiometric}
          webViewGetNativeCookie={webViewGetNativeCookie}
          webViewCheckWhatsappAvailable={webViewCheckWhatsappAvailable}
          webViewOpenWhatsapp={webViewOpenWhatsapp}
          webViewGetAuthConfig={webViewGetAuthConfig}
          webViewGetAvailableToBAuthMethods={webViewGetAvailableToBAuthMethods}
          webViewAuthWith3PServiceByToken={webViewAuthWith3PServiceByToken}
          webViewGetAppInfo={webViewGetAppInfo}
        />
      );
    };

    return ComponentWithWebView;
  };
}
